import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CircleCheck, CircleX } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { formatDateTime } from '@/lib/i18n';
import { apiError, ForecastMethod, ForecastTarget, type ForecastBacktest } from './lib';

/**
 * §23's acceptance criterion, on screen. ADR-0031 §2.3: a backtest scores the
 * chosen method AND seasonal naive on the SAME holdout, and `beatsBaseline`
 * is reported even when it is false — on the current seed data it IS false
 * (mae 1.8571 == baselineMae 1.8571), and this panel says so as plainly as
 * the backend's own `notes` sentence does. Dressing that up would spend a
 * manager's trust on a number that has not earned it.
 */
export default function BacktestPanel({
  branchId,
  target,
}: {
  branchId: string;
  target: ForecastTarget;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canManage = can('forecasting.manage');

  const [method, setMethod] = useState<ForecastMethod>(ForecastMethod.DAY_OF_WEEK_PROFILE);
  const [holdoutDays, setHoldoutDays] = useState('14');
  const [running, setRunning] = useState(false);

  const { data, loading, error, reload } = useApi<unknown>('/forecasting/backtests', {
    branchId,
    target,
  });
  const { rows } = rowsOf<ForecastBacktest>(data);
  const latest = rows[0];

  const run = async () => {
    setRunning(true);
    try {
      await api.post('/forecasting/backtests', {
        branchId,
        target,
        method,
        holdoutDays: Number(holdoutDays),
      });
      toast.success(t('forecasting.backtest.ran'));
      reload();
    } catch (e) {
      toast.error(apiError(e, t('forecasting.backtest.runFailed')));
    } finally {
      setRunning(false);
    }
  };

  const num = (value: string | null) =>
    value === null ? '—' : new Intl.NumberFormat(i18n.language || 'en', { maximumFractionDigits: 4 }).format(Number(value));

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{t('forecasting.backtest.title')}</CardTitle>
          <p className="text-sm text-ink-muted">{t('forecasting.backtest.subtitle')}</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t('forecasting.backtest.method')}>
              <Select value={method} onChange={(e) => setMethod(e.target.value as ForecastMethod)}>
                {[
                  ForecastMethod.DAY_OF_WEEK_PROFILE,
                  ForecastMethod.MOVING_AVERAGE,
                  ForecastMethod.SEASONAL_NAIVE,
                ].map((m) => (
                  <option key={m} value={m}>
                    {t(`forecasting.method.${m}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('forecasting.backtest.holdoutDays')}>
              <Input
                type="number"
                min={7}
                max={60}
                className="w-24"
                value={holdoutDays}
                onChange={(e) => setHoldoutDays(e.target.value)}
              />
            </Field>
            <Button size="sm" onClick={() => void run()} disabled={running}>
              {t('forecasting.backtest.run')}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={t('forecasting.backtest.loadFailed')} onRetry={reload} />
        ) : !latest ? (
          <EmptyState
            title={t('forecasting.backtest.empty')}
            hint={t('forecasting.backtest.emptyHint')}
          />
        ) : latest.insufficientData ? (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/40">
            <Icon as={CircleX} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="font-medium">{t('forecasting.backtest.insufficientData')}</p>
              <p className="mt-1 text-ink-muted">{latest.notes}</p>
            </div>
          </div>
        ) : (
          <>
            <div
              className={
                latest.beatsBaseline
                  ? 'flex items-start gap-3 rounded-lg border border-green-300 bg-green-50 p-4 text-sm dark:border-green-900 dark:bg-green-950/40'
                  : 'flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/40'
              }
            >
              <Icon
                as={latest.beatsBaseline ? CircleCheck : CircleX}
                className={
                  latest.beatsBaseline
                    ? 'mt-0.5 shrink-0 text-green-600 dark:text-green-400'
                    : 'mt-0.5 shrink-0 text-amber-600 dark:text-amber-400'
                }
              />
              <div>
                <p className="font-medium">
                  {t(
                    latest.beatsBaseline
                      ? 'forecasting.backtest.beats'
                      : 'forecasting.backtest.doesNotBeat',
                  )}
                </p>
                {/* The backend's own verdict, verbatim — never re-worded softer. */}
                <p className="mt-1 text-ink-muted">{latest.notes}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label={t('forecasting.backtest.mae')} value={num(latest.mae)} />
              <Stat
                label={t('forecasting.backtest.baselineMae')}
                value={num(latest.baselineMae)}
                muted
              />
              <Stat label={t('forecasting.backtest.mape')} value={latest.mape ? `${num(latest.mape)}%` : '—'} />
              <Stat label={t('forecasting.backtest.sampleSize')} value={String(latest.sampleSize)} />
            </div>
          </>
        )}

        {rows.length > 1 && (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('forecasting.backtest.ranAt')}</TH>
                  <TH>{t('forecasting.backtest.method')}</TH>
                  <TH>{t('forecasting.backtest.mae')}</TH>
                  <TH>{t('forecasting.backtest.baselineMae')}</TH>
                  <TH>{t('forecasting.backtest.beatsBaseline')}</TH>
                </TR>
              </THead>
              <tbody>
                {rows.slice(1, 8).map((b) => (
                  <TR key={b.id}>
                    <TD className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(b.ranAt, i18n.language)}
                    </TD>
                    <TD>{t(`forecasting.method.${b.method}`)}</TD>
                    <TD className="tabular-nums">{num(b.mae)}</TD>
                    <TD className="tabular-nums text-ink-muted">{num(b.baselineMae)}</TD>
                    <TD>
                      {b.insufficientData ? (
                        <Badge tone="neutral">{t('forecasting.backtest.insufficientDataShort')}</Badge>
                      ) : (
                        <Badge tone={b.beatsBaseline ? 'success' : 'warning'}>
                          {t(b.beatsBaseline ? 'common.yes' : 'common.no')}
                        </Badge>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-ink-muted">{label}</p>
      <p className={muted ? 'mt-1 text-lg font-semibold text-ink-muted tabular-nums' : 'mt-1 text-lg font-semibold tabular-nums'}>
        {value}
      </p>
    </div>
  );
}
